import {
  Button,
  Callout,
  Card,
  FilterableSelect,
  FormLayout,
  GridFieldGroup,
  Heading,
  LoadingSpinner,
  Paragraph,
  RadioGroup,
  SyntaxIcon,
  TextInput,
} from '@expressable/syntax-ds';
import { zodResolver } from '@hookform/resolvers/zod';
import { MagnifyingGlassIcon, StethoscopeIcon } from '@phosphor-icons/react';
import { HeadingCombo } from 'domains/signupDirect/SyntaxComps/HeadingCombo';
import { FC, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SignupError } from 'utils/errors/debuggableError';
import { stateOptions } from 'utils/states';
import { capitalizeAll } from 'utils/strings/capitalize';
import { NpiPhysician } from '../NPI/NPILookup';
import { useNPILookup } from '../NPI/queries';
import {
  AutoPCPFormType,
  PCPPickNPIFormType,
  PCPSearchFormType,
  PCPSearchSchema,
  PickNPIPhysicianSchema,
} from '../pcpSchemas';
import { ConfirmPCPDetails } from './ConfirmPCP';

export const SearchPCPForm: FC<{
  disabled?: boolean;
  onTriggerManualMode: () => void;
  onBailoutNoPCP: () => void;

  currentPCPData?: AutoPCPFormType;
  onChangeNPIPCP: (npiPhysician?: NpiPhysician) => void;

  isError?: boolean;
}> = ({ disabled, onTriggerManualMode, onBailoutNoPCP, currentPCPData, onChangeNPIPCP, isError }) => {
  const [searchParams, setSearchParams] = useState<PCPSearchFormType>();

  const { t } = useTranslation();
  const { data, isLoading } = useNPILookup(
    searchParams
      ? {
          disabled: false,
          query: searchParams,
        }
      : {
          disabled: true,
        },
  );

  const isFormDisabled = isLoading || disabled;

  const methods = useForm<PCPSearchFormType>({
    resolver: zodResolver(PCPSearchSchema),
  });

  const { handleSubmit } = methods;

  const foundPhysicians = data && data.length > 0;

  return (
    <div className="flex flex-col gap-e40">
      <FormProvider {...methods}>
        <HeadingCombo>
          <Heading noDefaultMargins level="h5">
            {t('directSignup.NPILookup.title')}
          </Heading>
          <Paragraph variant="secondary" size="md">
            {t('directSignup.NPILookup.subTitle')}
          </Paragraph>
        </HeadingCombo>
        <Callout type="info">{t('directSignup.NPILookup.callout')}</Callout>
        <Card className="p-0 mobile-d:p-0 divide-y divide-warm-gray-200 w-full">
          <div className="p-e32 flex flex-col gap-e16">
            <div className="flex gap-e16 items-center">
              <div className="size-e40 bg-purple-50 text-purple-500 rounded-full flex items-center justify-center p-e10 shrink-0">
                <SyntaxIcon icon={StethoscopeIcon} className="size-full" weight="fill" />
              </div>

              <HeadingCombo>
                <Heading noDefaultMargins level="h6">
                  {currentPCPData
                    ? t('directSignup.NPILookup.confirmPCP.title')
                    : t('directSignup.NPILookup.searchPCP.title')}
                </Heading>
                {!currentPCPData && (
                  <Paragraph variant="secondary" size="xs">
                    {t('directSignup.NPILookup.searchPCP.subTitle')}
                  </Paragraph>
                )}
              </HeadingCombo>
            </div>

            {isError && <Callout type="warning">{t('directSignup.NPILookup.sectionMissingError')}</Callout>}
          </div>

          {currentPCPData && (
            <ConfirmPCPDetails PCPData={currentPCPData} onDiscardPCP={() => onChangeNPIPCP(undefined)} />
          )}
          {!currentPCPData && (
            <>
              <FormLayout className="mobile-xs-d:gap-e40 tablet-d:gap-e56 items-stretch p-e32" variant="compact">
                <SearchFormFields disabled={isFormDisabled} />
                <FormLayout.CTA>
                  <Button variant="secondary" onClick={handleSubmit(setSearchParams)} disabled={isFormDisabled}>
                    <SyntaxIcon icon={MagnifyingGlassIcon} />
                    {t('directSignup.NPILookup.submit')}
                  </Button>

                  <Button variant="link" onClick={onBailoutNoPCP} disabled={isFormDisabled}>
                    {t('directSignup.NPILookup.bailout.dontHave')}
                  </Button>
                </FormLayout.CTA>
              </FormLayout>
              {foundPhysicians && (
                <PhysicianPicker NPIData={data} onBailout={onBailoutNoPCP} onSubmitNPI={onChangeNPIPCP} />
              )}
            </>
          )}

          {isLoading && <LoadingSpinner message={t('directSignup.NPILookup.loading')} className="p-e32" />}

          {!foundPhysicians && !isLoading && searchParams && (
            <NoPhysiciansFoundState onEnterPCPManually={onTriggerManualMode} />
          )}
        </Card>
      </FormProvider>
    </div>
  );
};

const SearchFormFields: FC<{ disabled?: boolean }> = ({ disabled }) => {
  const { register } = useFormContext<PCPSearchFormType>();
  const { t } = useTranslation();

  return (
    <>
      <GridFieldGroup>
        <TextInput
          {...register('firstName')}
          label={t('directSignup.NPILookup.inputs.firstName')}
          disabled={disabled}
        />
        <TextInput {...register('lastName')} label={t('directSignup.NPILookup.inputs.lastName')} disabled={disabled} />
        <TextInput {...register('city')} label={t('directSignup.NPILookup.inputs.city')} disabled={disabled} />

        <FilterableSelect
          data-testid="state-select"
          label={t('directSignup.NPILookup.inputs.state')}
          options={stateOptions}
          name="stateCode"
          disabled={disabled}
        />
      </GridFieldGroup>
    </>
  );
};

const PhysicianPicker = ({
  NPIData,
  onBailout,
  onSubmitNPI,
}: {
  NPIData: NpiPhysician[];
  onBailout: () => void;
  onSubmitNPI: (physician: NpiPhysician) => void;
}) => {
  if (NPIData.length === 0) throw new SignupError('Expected NPI data to have at least one entry');
  const methods = useForm<PCPPickNPIFormType>({
    resolver: zodResolver(PickNPIPhysicianSchema),
  });
  const { t } = useTranslation();
  const { handleSubmit } = methods;

  const onSubmit = (d: PCPPickNPIFormType) => {
    const NPINumber = d.pickedPhysicianNPICode;
    const NPIPhysician = NPIData.find(d => d.npiNumber === NPINumber);
    if (!NPIPhysician) throw new SignupError('NPIPhysician not found', { debugData: { NPINumber, NPIData } });
    onSubmitNPI(NPIPhysician);
  };
  return (
    <FormProvider {...methods}>
      <div className="p-e32">
        <RadioGroup
          label={t('directSignup.NPILookup.options.label', { amount: NPIData.length })}
          hint={t('directSignup.NPILookup.options.hint')}
          className="max-h-[480px] overflow-auto overflow-x-hidden"
          name="pickedPhysicianNPICode"
        >
          {NPIData.map(d => (
            <RadioGroup.Option value={d.npiNumber} key={d.npiNumber} description={d.description}>
              {capitalizeAll(`${d.fullName}, ${d.state} ${d.npiNumber}`)}
            </RadioGroup.Option>
          ))}
        </RadioGroup>
      </div>

      <FormLayout.CTA className="p-e32">
        <Button onClick={handleSubmit(onSubmit)}>{t('directSignup.NPILookup.confirmNPI')}</Button>
        <Button variant="link" onClick={onBailout}>
          {t('directSignup.NPILookup.bailout.cantFind')}
        </Button>
      </FormLayout.CTA>
    </FormProvider>
  );
};

const NoPhysiciansFoundState = ({ onEnterPCPManually }: { onEnterPCPManually: () => void }) => {
  const { t } = useTranslation();

  return (
    <div className="p-e32 flex flex-col gap-e20">
      <HeadingCombo>
        <Heading level="h6" noDefaultMargins>
          {t('directSignup.NPILookup.noPhysiciansFound.title')}
        </Heading>
        <Paragraph size="xs" variant="secondary">
          {t('directSignup.NPILookup.noPhysiciansFound.description')}
        </Paragraph>
      </HeadingCombo>

      <Button variant="secondary" onClick={onEnterPCPManually}>
        {t('directSignup.NPILookup.noPhysiciansFound.enterManually')}
      </Button>
    </div>
  );
};
